import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { cleanNotifications, showNotification } from "@mantine/notifications";
import {
    Client,
    ClientBillingInput,
    ClientCredits,
    ClientSettingsInput,
    ClientUser,
    ClientUserInput,
    ClientUserRole,
    Invoice,
    Tenant,
} from "src/graphql/generated/graphql";
import {
    chargeCreditsMutation,
    deactivateClientUserMutation,
    inviteClientUserMutation,
    issueChargeCreditsInvoiceMutation,
    requestTenantCreationMutation,
    updateClientBillingMutation,
    updateClientSettingsMutation,
} from "src/graphql/operations/client/mutations";
import {
    clientCreditsQuery,
    clientInvoicesQuery,
    clientQuery,
    clientsQuery,
    clientTenantQuery,
    clientTenantsQuery,
    clientUsersQuery,
} from "src/graphql/operations/client/queries";
import { graphqlFetch } from "src/graphql/utils/graphqlFetch";
import { GraphQLRequestError } from "src/graphql/utils/utils";
import { ClientLocale } from "src/types";
import { TranslationKeys } from "src/types/i18next";

import { handleError } from "../utils/error";
import { useAuthContext } from "./auth";

export const useClients = () => {
    const [clients, setClients] = useState<Client[]>([]);
    const [clientsTotal, setClientsTotal] = useState(0);
    const [isLoadingClients, setIsLoadingClients] = useState(false);

    const fetchClients = useCallback(async () => {
        setIsLoadingClients(true);

        try {
            const response = await graphqlFetch(clientsQuery);

            setClients(response.clients?.items || []);
            setClientsTotal(response.clients?.items?.length || 0);
        } catch (err) {
            handleError(err, { silent: true });
        } finally {
            setIsLoadingClients(false);
        }
    }, []);

    useEffect(() => {
        setClients([]);
        setClientsTotal(0);
        fetchClients();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return {
        clients,
        clientsTotal,
        isLoadingClients,
        reloadClients() {
            setClients([]);
            setClientsTotal(0);
            fetchClients();
        },
    };
};

export const useClient = (clientId: string) => {
    const [client, setClient] = useState<Client>();
    const [isLoadingClient, setIsLoadingClient] = useState(false);

    const fetchClient = useCallback(async (clientId: string) => {
        setIsLoadingClient(true);

        try {
            const response = await graphqlFetch(clientQuery, { clientId });

            setClient(response.client);
        } catch (err) {
            handleError(err, { context: { clientId }, silent: true });
        } finally {
            setIsLoadingClient(false);
        }
    }, []);

    useEffect(() => {
        setClient(null);

        if (!clientId) {
            return;
        }

        fetchClient(clientId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [clientId]);

    return {
        client,
        isLoadingClient,
        reloadClient() {
            setClient(null);

            if (!clientId) {
                return;
            }

            fetchClient(clientId);
        },
    };
};

export const useClientUsers = (clientId: string) => {
    const [clientUsers, setClientUsers] = useState<ClientUser[]>([]);
    const [isLoadingClientUsers, setIsLoadingClientUsers] = useState(false);

    // A reload while an earlier load is still paging must not add that load's pages to the new list.
    const latestLoad = useRef(0);

    const fetchClientUsers = useCallback(async (clientId: string, nextToken?: string, load = ++latestLoad.current) => {
        setIsLoadingClientUsers(true);

        let hasMore = false;
        try {
            const response = await graphqlFetch(clientUsersQuery, { clientId, nextToken });

            if (load !== latestLoad.current) {
                return;
            }

            // The pages are appended: client-api pages the group members.
            setClientUsers((previous) => [...previous, ...(response.client?.users?.items || [])]);

            if (response.client?.users?.nextToken) {
                hasMore = true;
                fetchClientUsers(clientId, response.client?.users?.nextToken, load);
            }
        } catch (err) {
            handleError(err, { context: { clientId }, silent: true });
        } finally {
            if (!hasMore && load === latestLoad.current) {
                setIsLoadingClientUsers(false);
            }
        }
    }, []);

    useEffect(() => {
        latestLoad.current++;
        setClientUsers([]);

        if (!clientId) {
            return;
        }

        fetchClientUsers(clientId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [clientId]);

    return {
        clientUsers,
        isLoadingClientUsers,
        reloadClientUsers() {
            latestLoad.current++;
            setClientUsers([]);

            if (!clientId) {
                return;
            }

            fetchClientUsers(clientId);
        },
    };
};

// The refusals client-api raises for deactivateClientUser, each with its own message. Any other
// failure gets the generic "could not deactivate" text with a support code.
const deactivationErrorKeys: Record<string, TranslationKeys> = {
    ClientUserSelfDeactivation: "clientuser_error_selfdeactivation",
    ClientOwnerProtected: "clientuser_error_ownerprotected",
    LastClientAdmin: "clientuser_error_lastadmin",
    ClientUserNotFound: "clientuser_error_notfound",
    ClientAccessChanged: "clientuser_error_accesschanged",
    ClientAccessDenied: "clientuser_error_accessdenied",
};

export const deactivationErrorKey = (err: unknown): TranslationKeys | undefined => {
    if (!(err instanceof GraphQLRequestError)) {
        return undefined;
    }

    const known = err.errorsNormalized.find((error) => deactivationErrorKeys[error.errorType]);

    return known ? deactivationErrorKeys[known.errorType] : undefined;
};

export const useDeactivateClientUser = (clientId: string) => {
    const [isDeactivatingClientUser, setIsDeactivatingClientUser] = useState(false);

    // Resolves true when the deactivation went through, so the caller can reload the list; a failure
    // is shown to the client here and resolves false.
    const deactivateClientUser = useCallback(
        async (clientUserId: string): Promise<boolean> => {
            setIsDeactivatingClientUser(true);

            try {
                await graphqlFetch(deactivateClientUserMutation, { clientId, clientUserId });

                return true;
            } catch (err) {
                handleError(err, {
                    message: {
                        key: deactivationErrorKey(err) ?? "clientuser_deactivating_failed",
                        namespace: "common",
                    },
                    context: { clientId, clientUserId },
                });

                return false;
            } finally {
                setIsDeactivatingClientUser(false);
            }
        },
        [clientId]
    );

    return {
        isDeactivatingClientUser,
        deactivateClientUser,
    };
};

export const useClientCredits = () => {
    const { client } = useAuthContext();

    const [clientCredits, setClientCredits] = useState<ClientCredits>();
    const [isLoadingClientCredits, setIsLoadingClientCredits] = useState(false);

    const fetchClientCredits = useCallback(async (clientId: string) => {
        setIsLoadingClientCredits(true);

        try {
            const response = await graphqlFetch(clientCreditsQuery, { clientId });

            setClientCredits(response.client?.credits);
        } catch (err) {
            handleError(err, { context: { clientId }, silent: true });
        } finally {
            setIsLoadingClientCredits(false);
        }
    }, []);

    useEffect(() => {
        setClientCredits(undefined);

        if (!client?.id) {
            return;
        }

        fetchClientCredits(client.id);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [client?.id]);

    return {
        clientCredits,
        isLoadingClientCredits,
        reloadClientCredits() {
            setClientCredits(undefined);

            if (!client?.id) {
                return;
            }

            fetchClientCredits(client.id);
        },
    };
};

export const useInvoices = (clientId: string) => {
    const [invoices, setInvoices] = useState<Invoice[]>([]);
    const [isLoadingInvoices, setIsLoadingInvoices] = useState(false);

    const fetchInvoices = useCallback(async (clientId: string, nextToken?: string) => {
        setIsLoadingInvoices(true);

        let hasMore = false;
        try {
            const response = await graphqlFetch(clientInvoicesQuery, { clientId, nextToken });

            setInvoices(response.client?.invoices?.items);

            if (response.client?.users?.nextToken) {
                hasMore = true;
                fetchInvoices(clientId, response.client?.invoices?.nextToken);
            }
        } catch (err) {
            handleError(err, { context: { clientId, nextToken }, silent: true });
        } finally {
            if (!hasMore) {
                setIsLoadingInvoices(false);
            }
        }
    }, []);

    useEffect(() => {
        setInvoices([]);

        if (!clientId) {
            return;
        }

        fetchInvoices(clientId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [clientId]);

    return {
        invoices,
        isLoadingInvoices,
        reloadInvoices() {
            setInvoices([]);

            if (!clientId) {
                return;
            }

            fetchInvoices(clientId);
        },
    };
};

export const useClientTenants = (clientId: string) => {
    const [clientTenants, setClientTenants] = useState<Tenant[]>([]);
    const [isLoadingClientTenants, setIsLoadingClientTenants] = useState(false);

    const fetchClientTenants = useCallback(async (clientId: string, nextToken?: string) => {
        setIsLoadingClientTenants(true);

        let hasMore = false;
        try {
            const response = await graphqlFetch(clientTenantsQuery, { clientId, nextToken });

            setClientTenants(response.client?.tenants?.items);

            if (response.client?.tenants?.nextToken) {
                hasMore = true;
                fetchClientTenants(clientId, response.client?.tenants?.nextToken);
            }
        } catch (err) {
            handleError(err, { context: { clientId, nextToken }, silent: true });
        } finally {
            if (!hasMore) {
                setIsLoadingClientTenants(false);
            }
        }
    }, []);

    useEffect(() => {
        setClientTenants([]);

        if (!clientId) {
            return;
        }

        fetchClientTenants(clientId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [clientId]);

    return {
        clientTenants,
        isLoadingClientTenants,
        reloadClientTenants() {
            setClientTenants([]);

            if (!clientId) {
                return;
            }

            fetchClientTenants(clientId);
        },
    };
};

export const useClientTenant = (tenantId: string) => {
    const [clientTenant, setClientTenant] = useState<Tenant>();
    const [isLoadingClientTenant, setIsLoadingClientTenant] = useState(false);

    const { client } = useAuthContext();

    const fetchClientTenant = useCallback(async (clientId: string, tenantId: string) => {
        setIsLoadingClientTenant(true);

        try {
            const response = await graphqlFetch(clientTenantQuery, { clientId, tenantId });

            setClientTenant(response.client?.tenant);
        } catch (err) {
            handleError(err, { context: { clientId, tenantId }, silent: true });
        } finally {
            setIsLoadingClientTenant(false);
        }
    }, []);

    useEffect(() => {
        setClientTenant(undefined);

        if (!client?.id || !tenantId) {
            return;
        }

        fetchClientTenant(client.id, tenantId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [client?.id, tenantId]);

    return {
        clientTenant,
        isLoadingClientTenant,
        reloadClientTenant() {
            setClientTenant(undefined);

            if (!client?.id || !tenantId) {
                return;
            }

            fetchClientTenant(client?.id, tenantId);
        },
    };
};

export const useUpdateClientSettings = (clientId: string) => {
    const { t } = useTranslation(["common"]);

    const [isUpdatingClientSettings, setIsUpdatingClientSettings] = useState(false);

    const updateClientSettings = useCallback(
        async (settings: ClientSettingsInput) => {
            setIsUpdatingClientSettings(true);
            showNotification({
                color: "blue",
                message: t("client_settings_saving"),
                loading: true,
            });

            try {
                const response = await graphqlFetch(updateClientSettingsMutation, { clientId, settings });

                cleanNotifications();
                return response.updateClientSettings;
            } catch (err) {
                handleError(err, {
                    message: { key: "client_settings_saving_failed", namespace: "common" },
                    context: { clientId, ...settings },
                });
            } finally {
                setIsUpdatingClientSettings(false);
            }
        },
        [t, clientId]
    );

    return {
        isUpdatingClientSettings,
        updateClientSettings,
    };
};

export const useUpdateClientBilling = (clientId: string) => {
    const { t } = useTranslation(["common"]);

    const [isUpdatingClientBilling, setIsUpdatingClientBilling] = useState(false);

    const updateClientBilling = useCallback(
        async (billing: ClientBillingInput) => {
            setIsUpdatingClientBilling(true);
            showNotification({
                color: "blue",
                message: t("client_billing_saving"),
                loading: true,
            });

            try {
                const response = await graphqlFetch(updateClientBillingMutation, { clientId, billing });

                cleanNotifications();
                return response.updateClientBilling;
            } catch (err) {
                handleError(err, {
                    message: { key: "client_billing_saving_failed", namespace: "common" },
                    context: { clientId },
                });
            } finally {
                setIsUpdatingClientBilling(false);
            }
        },
        [t, clientId]
    );

    return {
        isUpdatingClientBilling,
        updateClientBilling,
    };
};

export const useInviteClientUser = (clientId: string) => {
    const { t } = useTranslation(["common"]);

    const [isInvitingClientUser, setIsInvitingClientUser] = useState(false);

    const inviteClientUser = useCallback(
        async (clientUser: ClientUserInput, clientUserRole: ClientUserRole, clientUserLocale: ClientLocale) => {
            setIsInvitingClientUser(true);
            showNotification({
                color: "blue",
                message: t("clientuser_inviting"),
                loading: true,
            });

            try {
                const response = await graphqlFetch(inviteClientUserMutation, {
                    clientId,
                    clientUser,
                    clientUserRole,
                    clientUserLocale,
                });

                cleanNotifications();
                return response.inviteClientUser;
            } catch (err) {
                handleError(err, {
                    message: { key: "clientuser_inviting_failed", namespace: "common" },
                    context: { clientId, clientUserRole, clientUserLocale },
                });
            } finally {
                setIsInvitingClientUser(false);
            }
        },
        [t, clientId]
    );

    return {
        isInvitingClientUser,
        inviteClientUser,
    };
};

export const useRequestTenantCreation = () => {
    const { t } = useTranslation(["common"]);

    const { client } = useAuthContext();

    const [isRequestingTenantCreation, setIsRequestingTenantCreation] = useState(false);

    const requestTenantCreation = useCallback(
        async (message: string) => {
            if (!client?.id) {
                return;
            }

            setIsRequestingTenantCreation(true);
            showNotification({
                color: "blue",
                message: t("requestingtenantcreation"),
                loading: true,
            });

            try {
                await graphqlFetch(requestTenantCreationMutation, {
                    message,
                    clientId: client.id,
                });

                cleanNotifications();
            } catch (err) {
                handleError(err, {
                    message: { key: "requestingtenantcreation_failed", namespace: "common" },
                    context: { clientId: client.id, message },
                });
            } finally {
                setIsRequestingTenantCreation(false);
            }
        },
        [t, client?.id]
    );

    return {
        isRequestingTenantCreation,
        requestTenantCreation,
    };
};

export const useIssueChargeCreditsInvoice = () => {
    const { t } = useTranslation(["common"]);

    const { client } = useAuthContext();

    const [isIssuingChargeCreditsInvoice, setIsIssuingChargeCreditsInvoice] = useState(false);

    const issueChargeCreditsInvoice = useCallback(
        async (currency: string, amountCredits: number) => {
            if (!client?.id) {
                return;
            }

            setIsIssuingChargeCreditsInvoice(true);
            showNotification({
                color: "blue",
                message: t("issuingchargecreditsinvoice"),
                loading: true,
            });

            try {
                const response = await graphqlFetch(issueChargeCreditsInvoiceMutation, {
                    clientId: client.id,
                    currency,
                    amountCredits,
                });

                cleanNotifications();
                showNotification({
                    color: "green",
                    message: t("issuingchargecreditsinvoice_success"),
                });

                return response.issueChargeCreditsInvoice;
            } catch (err) {
                handleError(err, {
                    message: { key: "issuingchargecreditsinvoice_failed", namespace: "common" },
                    context: { clientId: client.id, currency, amountCredits },
                });
            } finally {
                setIsIssuingChargeCreditsInvoice(false);
            }
        },
        [t, client?.id]
    );

    return {
        isIssuingChargeCreditsInvoice,
        issueChargeCreditsInvoice,
    };
};

export const useChargeCredits = () => {
    const { client } = useAuthContext();

    const [isChargingCredits, setIsChargingCredits] = useState(false);

    const chargeCredits = useCallback(
        async (currency: string, amountCredits: number) => {
            if (!client?.id) {
                return;
            }

            setIsChargingCredits(true);

            try {
                const response = await graphqlFetch(chargeCreditsMutation, {
                    clientId: client.id,
                    currency,
                    amountCredits,
                });

                return response.chargeCredits;
            } catch (err) {
                handleError(err, {
                    message: { key: "issuingchargecreditsinvoice_failed", namespace: "common" },
                    context: { clientId: client.id, currency, amountCredits },
                });
            } finally {
                setIsChargingCredits(false);
            }
        },
        [client?.id]
    );

    return {
        isChargingCredits,
        chargeCredits,
    };
};
